import { useState } from "react";
import {
  X, CheckCircle, AlertCircle, Copy, Mail, User,
  BookOpen, Dumbbell, PlusCircle, Euro, UserX,
} from "lucide-react";

export default function CreateUserModal({ onClose, onCreated, api }) {
  const [name,             setName]             = useState("");
  const [email,            setEmail]            = useState("");
  const [hasCursus,        setHasCursus]        = useState(false);
  const [hasSchemaBuilder, setHasSchemaBuilder] = useState(false);
  const [price,            setPrice]            = useState("");
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState("");
  const [result,           setResult]           = useState(null);

  // "none" = leeg account zonder diensten
  const derivedPlan = hasCursus ? "full" : "none";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) { setError("Vul naam en e-mailadres in"); return; }
    setLoading(true); setError("");
    try {
      const data = await api.createUser(name, email, derivedPlan, price, hasSchemaBuilder);
      setResult(data);
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  const ACCESS_OPTIONS = [
    {
      key: "cursus", label: "Cursus",
      sub: "Toegang tot het volledige cursusmateriaal",
      icon: <BookOpen size={15} />, checked: hasCursus,
      toggle: () => setHasCursus(v => !v),
    },
    {
      key: "schema-builder", label: "Schema Maker",
      sub: "Zelf schema's aanmaken (normaal €100 eenmalig)",
      icon: <PlusCircle size={15} />, checked: hasSchemaBuilder,
      toggle: () => setHasSchemaBuilder(v => !v),
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-7 animate-fadeIn">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-white font-bold text-lg">Nieuw account</h2>
            <p className="text-zinc-600 text-xs mt-0.5">Stel naam, e-mail en toegang in</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
              <CheckCircle size={16} />
              {result.plan === "none" ? "Leeg account aangemaakt!" : "Account aangemaakt!"}
            </div>
            {result.plan === "none" && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-zinc-900/80 border border-white/6 text-zinc-400 text-xs">
                <UserX size={13} className="text-zinc-500 shrink-0" />
                Geen diensten — wijs een schema toe via "Schema Templates".
              </div>
            )}
            <div className="bg-[#1a1a1a] border border-[#C9A84C]/30 rounded-xl p-5">
              <p className="text-zinc-400 text-xs mb-2 uppercase tracking-widest font-bold">Toegangscode</p>
              <div className="flex items-center justify-between">
                <span className="text-[#C9A84C] text-2xl font-bold tracking-widest font-mono">{result.access_code}</span>
                <button onClick={() => navigator.clipboard.writeText(result.access_code)}
                  className="text-zinc-500 hover:text-[#C9A84C] transition-colors p-2"><Copy size={16} /></button>
              </div>
            </div>
            {result.email_sent
              ? <p className="text-zinc-400 text-sm flex items-center gap-2"><Mail size={14} className="text-[#C9A84C]" /> E-mail verstuurd naar {result.email}</p>
              : <p className="text-amber-500 text-sm flex items-center gap-2"><AlertCircle size={14} /> E-mail niet verstuurd — deel de code handmatig.</p>
            }
            <button onClick={onClose} className="btn-gold-course w-full py-3 font-bold text-sm uppercase tracking-wide">Sluiten</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="course-label">Volledige naam</label>
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  data-testid="create-user-name" placeholder="Jan de Vries" className="course-input pl-9" />
              </div>
            </div>
            <div>
              <label className="course-label">E-mailadres</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  data-testid="create-user-email" placeholder="jan@email.com" className="course-input pl-9" />
              </div>
            </div>
            <div>
              <label className="course-label">Toegang</label>
              <div className="space-y-2 mt-1">
                {ACCESS_OPTIONS.map(opt => (
                  <button key={opt.key} type="button" onClick={opt.toggle}
                    data-testid={`plan-option-${opt.key}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                      opt.checked
                        ? "border-[#C9A84C] bg-[#C9A84C]/8 text-white"
                        : "border-white/8 bg-[#0a0a0a] text-zinc-500 hover:border-white/20"
                    }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                      opt.checked ? "bg-[#C9A84C]/15 text-[#C9A84C]" : "bg-white/4 text-zinc-600"
                    }`}>{opt.icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold">{opt.label}</p>
                      <p className="text-xs opacity-60 mt-0.5">{opt.sub}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                      opt.checked ? "border-[#C9A84C] bg-[#C9A84C]" : "border-white/20 bg-transparent"
                    }`}>
                      {opt.checked && <CheckCircle size={12} className="text-black" />}
                    </div>
                  </button>
                ))}
              </div>

              {/* Leeg account indicator */}
              {!hasCursus && !hasSchemaBuilder && (
                <div className="flex items-center gap-2.5 mt-2.5 px-3.5 py-2.5 rounded-xl bg-zinc-900/80 border border-white/6">
                  <UserX size={13} className="text-zinc-500 shrink-0" />
                  <p className="text-zinc-500 text-xs leading-snug">
                    <span className="text-zinc-300 font-semibold">Leeg account</span> — geen diensten. Je kunt later handmatig een schema toewijzen via "Schema Templates".
                  </p>
                </div>
              )}
            </div>
            {/* Betaald bedrag — alleen relevant als er een dienst is */}
            {(hasCursus || hasSchemaBuilder) && (
            <div>
              <label className="course-label">
                Betaald bedrag <span className="ml-1.5 text-zinc-600 font-normal normal-case">(optioneel)</span>
              </label>
              <div className="relative">
                <Euro size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)}
                  data-testid="create-user-price" placeholder="399.95" className="course-input pl-9" />
              </div>
            </div>
            )}
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="btn-course-outline flex-1 py-3 text-sm font-semibold">Annuleren</button>
              <button type="submit" disabled={loading} data-testid="create-user-submit"
                className="btn-gold-course flex-1 py-3 text-sm font-bold uppercase tracking-wide">
                {loading ? "Aanmaken..." : (!hasCursus && !hasSchemaBuilder ? "Leeg account aanmaken" : "Account aanmaken")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
